import { Folder } from '@/types/folders/folder';

export class FolderStore {
  private folders: Map<string, Folder> = new Map();
  private counter: number = 1;

  create(data: {
    folder_name: string;
    parent_id?: string;
    organization_id?: string;
    category_id?: string;
  }): Folder {
    const now = new Date().toISOString();
    const folder: Folder = {
      id: `folder_${this.counter++}`,
      name: data.folder_name,
      created_at: now,
      updated_at: now,
      tags: [],
      created_by: 'user_mock',
      updated_by: 'user_mock',
      parent_id: data.parent_id || null,
      project_id: null,
      deleted_at: null,
      default: false,
      extras: {},
    };
    this.folders.set(folder.id, folder);
    return folder;
  }

  getAll(params?: {
    parent_id?: string;
    organization_id?: string;
    category_id?: string;
    project_id?: string;
  }): Folder[] {
    const folders: Folder[] = [];

    for (const folder of this.folders.values()) {
      if (folder.deleted_at) continue;

      if (params?.parent_id !== undefined) {
        if (folder.parent_id === params.parent_id) {
          folders.push(folder);
        }
      } else {
        if (!folder.parent_id) {
          folders.push(folder);
        }
      }
    }

    return folders;
  }

  getById(folderId: string): Folder | null {
    const folder = this.folders.get(folderId);
    return folder && !folder.deleted_at ? folder : null;
  }

  delete(folderId: string): boolean {
    const folder = this.folders.get(folderId);
    if (!folder) return false;

    folder.deleted_at = new Date().toISOString();
    folder.updated_at = new Date().toISOString();
    this.folders.set(folderId, folder);

    for (const childFolder of this.folders.values()) {
      if (childFolder.parent_id === folderId && !childFolder.deleted_at) {
        this.delete(childFolder.id);
      }
    }

    return true;
  }

  rename(folderId: string, folderName: string): Folder | null {
    const folder = this.folders.get(folderId);
    if (!folder || folder.deleted_at) return null;

    folder.name = folderName;
    folder.updated_at = new Date().toISOString();
    this.folders.set(folderId, folder);
    return folder;
  }

  move(folderId: string, targetFolderId: string | null): boolean {
    const folder = this.folders.get(folderId);
    if (!folder || folder.deleted_at) return false;

    folder.parent_id = targetFolderId;
    folder.updated_at = new Date().toISOString();
    this.folders.set(folderId, folder);
    return true;
  }

  updateTags(folderId: string, tags: string[]): Folder | null {
    const folder = this.folders.get(folderId);
    if (!folder || folder.deleted_at) return null;

    folder.tags = tags;
    folder.updated_at = new Date().toISOString();
    this.folders.set(folderId, folder);
    return folder;
  }

  getChildren(parentId: string | null): Folder[] {
    return this.getAll({ parent_id: parentId ?? undefined });
  }

  clear(): void {
    this.folders.clear();
    this.counter = 1;
  }

  getAllActive(): Folder[] {
    return Array.from(this.folders.values()).filter(f => !f.deleted_at);
  }

  initializeDefaultData(): Map<string, string> {
    const folderMap = new Map<string, string>();

    const documentsFolder = this.create({ folder_name: 'Documents' });
    folderMap.set('Documents', documentsFolder.id);

    const projectsFolder = this.create({ folder_name: 'Projects' });
    folderMap.set('Projects', projectsFolder.id);

    const archiveFolder = this.create({ folder_name: 'Archive' });
    folderMap.set('Archive', archiveFolder.id);

    const reportsFolder = this.create({
      folder_name: 'Reports',
      parent_id: documentsFolder.id,
    });
    folderMap.set('Reports', reportsFolder.id);

    const templatesFolder = this.create({
      folder_name: 'Templates',
      parent_id: documentsFolder.id,
    });
    folderMap.set('Templates', templatesFolder.id);

    const activeProjectsFolder = this.create({
      folder_name: 'Active Projects',
      parent_id: projectsFolder.id,
    });
    folderMap.set('Active Projects', activeProjectsFolder.id);

    const project2024Folder = this.create({
      folder_name: 'Project 2024',
      parent_id: activeProjectsFolder.id,
    });
    folderMap.set('Project 2024', project2024Folder.id);

    return folderMap;
  }
}
